import { Col, Form, Row } from "react-bootstrap";
import { FormEvent, useEffect, useState } from 'react'
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from 'react-router-dom';
import { ApiErrorResponse } from "../../../../types/ApiErrorResponse";
import { useAlert } from "../../../../hooks/useAlert";
import AlertMessage from "../../../../components/AlertMessage";
import { getClaimRequestTypeFormSchema } from "../schema/claimRequestTypeSchema";
import { GenericSaveButton } from "../../../../components/common/GenericSaveButton";
import { createClaimRequestType, getClaimRequestTypeById, updateClaimRequestType } from "../api/claimRequestTypeApi";
import { ClaimRequestTypeFormDto } from "../types";
import { TranslateDescription } from "../../../../types/TranslateDescription";

export default function ClaimRequestTypeForm() {
    const { t } = useTranslation(['common', 'claimRequestType']);
    const navigate = useNavigate();

    const { claimRequestTypeId } = useParams<{ claimRequestTypeId: string }>();
    const isEditMode = Boolean(claimRequestTypeId);

    const { alert, hideAlert, showAlert, isVisible } = useAlert({ fromLocation: false });
    const [errors, setErrors] = useState<{ [key: string]: string | undefined }>({});

    const [formData, setFormData] = useState<ClaimRequestTypeFormDto>({
        id: 0,
        description: {}
    })

    const changeInputField = (field: keyof ClaimRequestTypeFormDto) => (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        setFormData({
            ...formData,
            [field]: e.target.value
        });
    };

    const changeDescriptionField = (language: keyof TranslateDescription) => (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        setFormData({
            ...formData,
            description: {
                ...formData.description,
                [language]: e.target.value
            }
        });
    };

    useEffect(() => {
        if (claimRequestTypeId) {
            getClaimRequestTypeById(parseInt(claimRequestTypeId))
                .then(data => setFormData(data))
                .catch((err: ApiErrorResponse) => {
                    console.error(err);
                });
        }
    }, [claimRequestTypeId]);

    const handleSubmit = async (e?: FormEvent) => {
        e?.preventDefault();
        const schema = getClaimRequestTypeFormSchema(t);

        try {
            await schema.validate(formData, { abortEarly: false });
        } catch (err: any) {
            const validationErrors: { [key: string]: string | undefined } = {};
            err.inner?.forEach((error: any) => {
                if (error.path) {
                    validationErrors[error.path] = error.message;
                }
            });
            setErrors(validationErrors);
            return;
        }

        try {
            if (isEditMode) {
                await updateClaimRequestType(formData.id, formData);
                navigate('/admin/claimRequestTypes', {
                    state: { message: t("claimRequestType:new.newclaimRequestTypeSaveSuccessfully"), shouldRefresh: true }
                });
            }else{
                await createClaimRequestType(formData);
                navigate('/admin/claimRequestTypes', {
                    state: { message: t("claimRequestType:new.newclaimRequestTypeSaveSuccessfully"), shouldRefresh: true }
                });
            }
        } catch (error) {
            const fetchError = error as ApiErrorResponse;
            const errorMessage = fetchError?.message
            showAlert({
                message: errorMessage,
                type: "danger",
                duration: 5000,
            });
        }
    }

    return (
        <>
            <Row className="mb-2">
                <Col md={8}>
                    <h2>{isEditMode
                        ? t("claimRequestType:title")
                        : t("claimRequestType:buttonNew")}</h2>
                </Col>
                <Col xs={4} md={4} className="text-end">
                    <GenericSaveButton
                        onClick={handleSubmit}
                        onCancelClick={() => navigate('/admin/claimRequestTypes')}
                        cancelLabel={isEditMode ? t("common:buttons.buttonBack") : t("common:buttons.buttonCancel")}
                    />
                </Col>
                {isVisible && (
                    <AlertMessage
                        message={alert!.message}
                        type={alert!.type}
                        onClose={hideAlert}
                    />
                )}
            </Row>
            <Row>
                <Col>
                    <Form>
                        <Row>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>{t("common:form.fields.id")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={claimRequestTypeId}
                                        disabled={true}
                                    />
                                </Form.Group>
                            </Col>
                        
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>{t("claimRequestType:form.color")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formData.color}
                                        onChange={changeInputField('color')}
                                        isInvalid={!!errors["color"]}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.color || ""}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mt-2">
                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label>{t("genericDic:field1NewFormDescriptionCA")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formData.description.catalan}
                                        onChange={changeDescriptionField('catalan')}
                                        isInvalid={!!errors["description.catalan"]}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors["description.catalan"]}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mt-2">
                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label>{t("genericDic:field1NewFormDescriptionES")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formData.description.spanish}
                                        onChange={changeDescriptionField('spanish')}
                                        isInvalid={!!errors["description.spanish"]}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors["description.spanish"]}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mt-2">
                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label>{t("genericDic:field1NewFormDescriptionEN")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formData.description.english}
                                        onChange={changeDescriptionField('english')}
                                        isInvalid={!!errors["description.english"]}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors["description.english"]}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mt-2">
                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label>{t("genericDic:field1NewFormDescriptionFR")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formData.description.french}
                                        onChange={changeDescriptionField('french')}
                                        isInvalid={!!errors["description.french"]}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors["description.french"]}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mt-2">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>{t("claimRequestType:form.daysToRespond")}</Form.Label>
                                    <Form.Control
                                        type="number"
                                        value={formData.maximumDaysToRespond}
                                        onChange={changeInputField('maximumDaysToRespond')}
                                        isInvalid={!!errors["maximumDaysToRespond"]}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.maximumDaysToRespond || ""}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>{t("claimRequestType:form.daysToWarnOfDeadline")}</Form.Label>
                                    <Form.Control
                                        type="number"
                                        value={formData.daysToRemindDeadline}
                                        onChange={changeInputField('daysToRemindDeadline')}
                                        isInvalid={!!errors["daysToRemindDeadline"]}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.daysToRemindDeadline || ""}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mt-2">
                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label>{t("claimRequestType:form.definationText")}</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={5}
                                        value={formData.promptText}
                                        onChange={changeInputField('promptText')}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Form>
                </Col>
            </Row>
        </>
    );
}

